import type { MouseEvent } from 'react';
import React, { useContext, useState } from 'react';
import { css, styled } from '@compiled/react';
import { useIntl, defineMessages } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import { type GraphQLTaggedNode } from 'relay-runtime';

import EditIcon from '@atlaskit/icon/core/edit';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Text from '@atlaskit/primitives/text';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Pressable from '@atlaskit/primitives/pressable';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';

import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { HoverPreloader, PreloadingPresets } from '@confluence/hover-preloader';
import { SPACE_INDEX, SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { EditSpaceLogo } from '@confluence/edit-space-logo';
import type {
	ExternalCollaboratorsSpaceInformationMessageFragmentRef,
	ExternalCollaboratorsSpaceInformationMessageType,
} from '@confluence/space-guest-list/entry-points/ExternalCollaboratorsSpaceInformationMessage.ts';
import { LoadableAfterPaint } from '@confluence/loadable';
import { useRouteName } from '@confluence/route-manager/entry-points/RouteState.ts';
import { EDIT_SPACE_LOGO_DETAILS_LOAD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { EditorSpaceSpotlight } from '@confluence/experiment-nth-user-orientation/entry-points/EditorSpaceSpotlight.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';

import { ScrollToTopButton } from './ScrollToTopButton.tsx';
import { SpaceMoreActionsMenu } from './SpaceMoreActionsMenu.tsx';
import type { SpaceHeader_space$key } from './__generated__/SpaceHeader_space.graphql.ts';

const ExternalCollaboratorsSpaceInformationMessageLoader = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ExternalCollaboratorsSpaceInformationMessage" */ '@confluence/space-guest-list/entry-points/ExternalCollaboratorsSpaceInformationMessage.ts'
			)
		).ExternalCollaboratorsSpaceInformationMessage,
	name: 'ExternalCollaboratorsSpaceInformationMessage',
});

export const SpaceHeaderSpaceFragment: GraphQLTaggedNode = graphql`
	fragment SpaceHeader_space on Space @argumentDefinitions(isLicensed: { type: "Boolean!" }) {
		id
		key
		name
		type
		status
		icon {
			path
		}
		homepage {
			id
		}
		currentUser @include(if: $isLicensed) {
			isAdmin
			isFavourited
			isWatched
		}
		operations {
			operation
			targetType
		}
		containsExternalCollaborators
	}
`;

export type SpaceHeaderProps = {
	spaceId: string;
	spaceName: string;
	spaceKey: string;
	homepageId: string | undefined;
	iconPath: string | undefined;
	isStarred: boolean;
	isWatched: boolean;
	isSpaceAdmin: boolean;
	canManageSomeUsers: boolean;
	canAdminister: boolean;
	canManageContent: boolean;
	canManagePublicLinks: boolean;
	canViewAnalytics: boolean;
	isPersonalSpace: boolean;
	isSpaceArchived: boolean;
	hasDeleteSpacePermission: boolean;
	hasArchiveSpacePermission: boolean;
};

export type ExternalCollaboratorProps = {
	isExternalCollaborator: boolean;
	containsExternalCollaborators: boolean;
};

const MANAGE_USERS = 'manage_users';
const MANAGE_NONLICENSED_USERS = 'manage_nonlicensed_users';
const MANAGE_GUEST_USERS = 'manage_guest_users';
const ARCHIVE = 'archive';
const ADMINISTER = 'administer';
const MANAGE_CONTENT = 'manage_content';
const MANAGE_PUBLIC_LINKS = 'manage_public_links';
const VIEW_ANALYTICS = 'view_analytics';

type SpaceHeaderComponentProps =
	| (SpaceHeaderProps &
			ExternalCollaboratorProps & {
				spaceFragmentRef?: never;
				externalCollaboratorsSpaceFragmentRef?: ExternalCollaboratorsSpaceInformationMessageFragmentRef | null;
			})
	| {
			spaceFragmentRef: SpaceHeader_space$key;
			isExternalCollaborator: boolean;
			externalCollaboratorsSpaceFragmentRef?: ExternalCollaboratorsSpaceInformationMessageFragmentRef | null;
	  };

const SpaceHeaderFromFragment = ({
	spaceFragmentRef,
	isExternalCollaborator,
	externalCollaboratorsSpaceFragmentRef,
}: {
	spaceFragmentRef: SpaceHeader_space$key;
	isExternalCollaborator: boolean;
	externalCollaboratorsSpaceFragmentRef?: ExternalCollaboratorsSpaceInformationMessageFragmentRef | null;
}): React.JSX.Element => {
	const space = useFragment(SpaceHeaderSpaceFragment, spaceFragmentRef);
	const operations = space.operations ?? [];
	const showExCoMessaging = !isExternalCollaborator && space.containsExternalCollaborators;

	return (
		<SpaceHeaderInner
			spaceId={space.id ?? ''}
			spaceName={space.name ?? ''}
			spaceKey={space.key ?? ''}
			homepageId={space.homepage?.id ?? undefined}
			iconPath={`${CONTEXT_PATH}${space.icon?.path}`}
			isStarred={Boolean(space.currentUser?.isFavourited)}
			isWatched={Boolean(space.currentUser?.isWatched)}
			isSpaceAdmin={space.currentUser?.isAdmin ?? false}
			canManageSomeUsers={operations.some(
				(operation) =>
					operation?.operation === MANAGE_USERS ||
					operation?.operation === MANAGE_NONLICENSED_USERS ||
					operation?.operation === MANAGE_GUEST_USERS,
			)}
			canAdminister={operations.some(
				(operation) => operation?.operation === ADMINISTER && operation?.targetType === 'space',
			)}
			canManageContent={operations.some(
				(operation) => operation?.operation === MANAGE_CONTENT && operation?.targetType === 'space',
			)}
			canManagePublicLinks={operations.some(
				(operation) =>
					operation?.operation === MANAGE_PUBLIC_LINKS && operation?.targetType === 'space',
			)}
			canViewAnalytics={operations.some(
				(operation) => operation?.operation === VIEW_ANALYTICS && operation?.targetType === 'space',
			)}
			isPersonalSpace={space.type === 'personal'}
			showExCoMessaging={showExCoMessaging}
			externalCollaboratorsSpaceFragmentRef={externalCollaboratorsSpaceFragmentRef}
			isSpaceArchived={space.status === 'archived'}
			hasDeleteSpacePermission={operations.some(
				(operation) => operation?.operation === 'delete' && operation?.targetType === 'space',
			)}
			hasArchiveSpacePermission={operations.some(
				(operation) => operation?.operation === ARCHIVE && operation?.targetType === 'space',
			)}
		/>
	);
};

export const SpaceHeader = (props: SpaceHeaderComponentProps): React.JSX.Element => {
	if (props.spaceFragmentRef) {
		return (
			<SpaceHeaderFromFragment
				spaceFragmentRef={props.spaceFragmentRef}
				isExternalCollaborator={props.isExternalCollaborator}
				externalCollaboratorsSpaceFragmentRef={props.externalCollaboratorsSpaceFragmentRef}
			/>
		);
	}

	const {
		isExternalCollaborator,
		containsExternalCollaborators,
		externalCollaboratorsSpaceFragmentRef,
		...spaceHeaderProps
	} = props;
	const showExCoMessaging = !isExternalCollaborator && containsExternalCollaborators;

	return (
		<SpaceHeaderInner
			{...spaceHeaderProps}
			showExCoMessaging={showExCoMessaging}
			externalCollaboratorsSpaceFragmentRef={externalCollaboratorsSpaceFragmentRef}
		/>
	);
};

const SpaceHeaderInner = ({
	spaceId,
	spaceName,
	spaceKey,
	homepageId,
	iconPath,
	isStarred,
	isWatched,
	isSpaceAdmin,
	canManageSomeUsers,
	canAdminister,
	canManageContent,
	canManagePublicLinks,
	canViewAnalytics,
	isPersonalSpace,
	showExCoMessaging,
	isSpaceArchived,
	hasDeleteSpacePermission,
	hasArchiveSpacePermission,
	externalCollaboratorsSpaceFragmentRef,
}: SpaceHeaderProps & {
	showExCoMessaging: boolean;
	externalCollaboratorsSpaceFragmentRef?: ExternalCollaboratorsSpaceInformationMessageFragmentRef | null;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isSelected = useRouteName(isOnSpaceRouteArgs);

	const spaceOverviewHref = SPACE_OVERVIEW.toUrl(
		{ spaceKey },
		homepageId
			? {
					query: { [SPACE_OVERVIEW.HOMEPAGE_ID_QUERY_KEY]: homepageId },
				}
			: undefined,
	);

	const description = showExCoMessaging ? (
		expVal('cc_perms_fix_guests_css_safari', 'isEnabled', false) ? (
			<div css={exCoMessageContainerStyles}>
				<ExternalCollaboratorsSpaceInformationMessageLoader
					analyticsSource="spaceHeader"
					messageType={'IN_SPACE' as ExternalCollaboratorsSpaceInformationMessageType}
					shouldShowMessageIcon={false}
					shouldShowAdditionalMessageContext={false}
					spaceKey={spaceKey}
					spaceFragmentRef={externalCollaboratorsSpaceFragmentRef}
					isSubtle
				/>
			</div>
		) : (
			<Text weight="medium">
				<ExternalCollaboratorsSpaceInformationMessageLoader
					analyticsSource="spaceHeader"
					messageType={'IN_SPACE' as ExternalCollaboratorsSpaceInformationMessageType}
					shouldShowMessageIcon={false}
					shouldShowAdditionalMessageContext={false}
					spaceKey={spaceKey}
					spaceFragmentRef={externalCollaboratorsSpaceFragmentRef}
					isSubtle
				/>
			</Text>
		)
	) : undefined;

	const onClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'spaceHeader',
				source: 'sideNavigation',
				attributes: {
					navVersion: 4,
				},
			},
		}).fire();
	};

	const [isMoreActionsOpen, setIsMoreActionsOpen] = useState(false);

	const moreActionsMenu = (
		<SpaceMoreActionsMenu
			isOpen={isMoreActionsOpen}
			setIsOpen={setIsMoreActionsOpen}
			spaceId={spaceId}
			spaceKey={spaceKey}
			isStarred={isStarred}
			isWatched={isWatched}
			isSpaceAdmin={isSpaceAdmin}
			canManageSomeUsers={canManageSomeUsers}
			canAdminister={canAdminister}
			canManageContent={canManageContent}
			canManagePublicLinks={canManagePublicLinks}
			canViewAnalytics={canViewAnalytics}
			isSpaceArchived={isSpaceArchived}
			hasDeleteSpacePermission={hasDeleteSpacePermission}
			hasArchiveSpacePermission={hasArchiveSpacePermission}
		/>
	);

	return (
		<>
			<Box xcss={stickyWrapper} data-vc="Nav4-space-header">
				<SpaceHeaderContainer>
					<EditorSpaceSpotlight>
						<>
							<HoverPreloader
								name="space-header"
								preloadUrl={spaceOverviewHref}
								preloadOptions={PreloadingPresets.onlyJSAndCriticalData}
							>
								<LinkMenuItem
									href={spaceOverviewHref}
									isSelected={isSelected}
									onClick={onClick}
									elemBefore={
										<EditableSpaceIcon
											spaceId={spaceId}
											spaceKey={spaceKey}
											iconPath={iconPath}
											isSpaceAdmin={isSpaceAdmin}
											isPersonalSpace={isPersonalSpace}
										/>
									}
									actions={moreActionsMenu}
									// @ts-expect-error Platform component currently only accepts a string; we're passing an element anyway.
									description={description}
								>
									{spaceName}
								</LinkMenuItem>
							</HoverPreloader>
						</>
					</EditorSpaceSpotlight>
				</SpaceHeaderContainer>
			</Box>
			{/**
			 * this is the scroll to top button
			 **/}
			<ScrollToTopButton showExCoMessaging={showExCoMessaging} />
		</>
	);
};

type EditableSpaceIconProps = Pick<
	SpaceHeaderProps,
	'spaceId' | 'spaceKey' | 'iconPath' | 'isSpaceAdmin' | 'isPersonalSpace'
>;

const EditableSpaceIcon = ({
	spaceId,
	spaceKey,
	iconPath,
	isSpaceAdmin,
	isPersonalSpace,
}: EditableSpaceIconProps) => {
	const intl = useIntl();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [shouldRenderDialog, setShouldRenderDialog] = useState(false);

	const image = iconPath ? (
		<img
			src={iconPath}
			alt=""
			css={icon}
			data-testid="space-icon"
			data-vc="space-header-icon-image"
			data-ssr-placeholder-ignored
			role="presentation"
		/>
	) : null;

	const canEdit = isSpaceAdmin && !isPersonalSpace;
	if (!canEdit) {
		return image;
	}

	const editLabel = intl.formatMessage(i18n.editSpaceDetails);

	const onClick = (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		experienceTracker.start({
			name: EDIT_SPACE_LOGO_DETAILS_LOAD_EXPERIENCE,
			timeout: 10000,
		});

		setShouldRenderDialog(true);
		setIsDialogOpen(true);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'editSpaceDetails',
				source: 'sideNavSpaceHeader',
				containerType: 'space',
				containerId: spaceId,
				attributes: {
					navVersion: 4,
					entryPoint: 'sideNav',
					spaceKey,
				},
			},
		}).fire();
	};

	const onFocus = () => {
		setShouldRenderDialog(true);
	};

	const onMouseEnter = () => {
		setShouldRenderDialog(true);
	};

	const onDialogOpen = () => {
		setIsDialogOpen(true);
	};

	const onDialogClose = () => {
		setIsDialogOpen(false);
	};

	return (
		<>
			<Tooltip content={editLabel}>
				<Pressable
					xcss={fg('confluence_frontend_tokenize_border') ? iconButton : iconButtonFallback}
					aria-label={editLabel}
					onClick={onClick}
					onFocus={onFocus}
					onMouseEnter={onMouseEnter}
				>
					{image}
					<Box xcss={editIconWrapper}>
						<EditIcon label="" color={token('color.icon.inverse')} />
					</Box>
				</Pressable>
			</Tooltip>
			{shouldRenderDialog && (
				<EditSpaceLogo
					spaceKey={spaceKey}
					isOpen={isDialogOpen}
					onOpen={onDialogOpen}
					onClose={onDialogClose}
					entryPoint="sideNav"
				/>
			)}
		</>
	);
};

const isOnSpaceRouteArgs = {
	selector: (routeName: string | undefined) =>
		routeName === SPACE_INDEX.name || routeName === SPACE_OVERVIEW.name,
};

// Safari workaround: the LinkMenuItem `description` slot applies
// `-webkit-line-clamp: 1` which triggers a WebKit bug that clips inline-flex
// children (the "1 guest" button). Nesting our own `-webkit-box` with
// `line-clamp: 2` gives the content enough room that the clamp engine never
// has to truncate, avoiding the bug.
const exCoMessageContainerStyles = css({
	display: '-webkit-box',
	WebkitBoxOrient: 'vertical',
	WebkitLineClamp: 2,
	overflow: 'hidden',
	width: '100%',
});

const stickyWrapper = xcss({
	position: 'sticky',
	top: 'space.0',
	zIndex: 'navigation', // Needs to be at least z-index 2 to appear on top of Content and Shortcuts
});

const icon = css({
	width: '24px',
	height: '24px',
	borderRadius: token('radius.medium'),
});

const iconButton = xcss({
	border: token('space.0'),
	borderRadius: 'radius.small',
	background: 'none',
	padding: 'space.0',
	position: 'relative',
	width: '24px',
	height: '24px',
	cursor: 'pointer',
	marginTop: 'space.050',
});

const iconButtonFallback = xcss({
	border: 'none',
	borderRadius: 'radius.small',
	background: 'none',
	padding: 'space.0',
	position: 'relative',
	width: '24px',
	height: '24px',
	cursor: 'pointer',
	marginTop: 'space.050',
});

const editIconWrapper = xcss({
	position: 'absolute',
	height: '24px',
	width: '24px',
	top: 'space.0',
	left: 'space.0',
	padding: 'space.050',
	opacity: 0,
	transition: 'opacity 0.5s ease',
	':hover': {
		opacity: 1,
	},
	':focus': {
		opacity: 1,
	},
	backgroundColor: 'color.blanket',
	borderRadius: 'radius.medium',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpaceHeaderContainer = styled.div({
	// The padding here is weird because of the sticky/absolute borders.
	// The padding top and bottom 'should' be 12px/space.150, but the borders add 1px each, so the padding is actually 11px.
	// But the top border is positioned on top of the element, so the top padding must stay 12px to vertically center the content.
	// But the bottom border is positioned below the element, so the bottom padding must be 11px otherwise the border will be hidden.

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	paddingTop: token('space.150'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingBottom: '11px',
	backgroundColor: token('elevation.surface'),
});

const i18n = defineMessages({
	editSpaceDetails: {
		id: 'side-navigation.space-navigation.header.edit-space-details',
		defaultMessage: 'Edit space details',
		description: 'Text for a tooltip and label indicating where to click to edit space details',
	},
});
